import { Component, inject } from "@angular/core";
import { Router } from "@angular/router";
import { UiKitModule } from "@app/ui-kit.module";
import { CloudProjectSelectModalComponent } from "@components/project-import/cloud-project-select-modal/cloud-project-select-modal.component";
import {
  ProjectImportConfirmModalComponent,
  ProjectImportConfirmResult,
} from "@components/project-import/confirm-modal/project-import-confirm-modal.component";
import { ProductCountryService } from "@domain/product/product-country.service";
import { ProjectApiService } from "@domain/project/api/project-api.service";
import { CloudProjectMetadata } from "@domain/project/cloud-project-metadata";
import { requireScopedProject } from "@domain/project/project-deserializer";
import { ProjectFloorplanService } from "@domain/project/project-floorplan.service";
import type { ScopedProject } from "@domain/project/project-guards";
import { ProjectImageService } from "@domain/project/project-image.service";
import { ProjectMigrationService } from "@domain/project/project-migration.service";
import { ProjectService } from "@domain/project/project.service";
import { ModalService, ModalSize } from "@odx/angular/components/modal";
import { ToastHelper } from "@utils/toast-helper.service";
import { catchError, forkJoin, map, of, switchMap, tap } from "rxjs";

@Component({
  selector: "app-project-import",
  templateUrl: "./project-import.component.html",
  styleUrls: ["./project-import.component.scss"],
  imports: [UiKitModule],
  standalone: true,
})
export class ProjectImportComponent {
  private router = inject(Router);
  private modalService = inject(ModalService);
  private toastHelper = inject(ToastHelper);
  private projectService = inject(ProjectService);
  private apiService = inject(ProjectApiService);
  private productCountryService = inject(ProductCountryService);
  private projectImageService = inject(ProjectImageService);
  private projectFloorplanService = inject(ProjectFloorplanService);
  private projectMigrationService = inject(ProjectMigrationService);

  protected readonly navigator = navigator;

  importCloudProject() {
    this.modalService
      .open(CloudProjectSelectModalComponent, { size: ModalSize.LARGE })
      .onClose$.pipe(
        switchMap(({ project, metadata }: { project: ScopedProject; metadata: CloudProjectMetadata }) => {
          return this.resolveExistingProject(project).pipe(
            tap((existingProject) => {
              this.importProject(project, true, existingProject, metadata);
            }),
          );
        }),
      )
      .subscribe();
  }

  importProjectAndMetadataFromCloud(projectId: string) {
    return forkJoin([this.projectService.loadProjectFromCloud(projectId), this.apiService.getMetadata(projectId)]).pipe(
      switchMap(([project, metadata]) =>
        this.projectImageService.getProjectImages(projectId, project.id).pipe(
          catchError(() => of(null)),
          switchMap(() => this.projectFloorplanService.getProjectFloorplans(project).pipe(catchError(() => of(null)))),
          switchMap(() => this.projectMigrationService.runMigrations(project, (p) => this.projectService.update(p))),
          map((migratedProject) => {
            this.importProject(migratedProject, true, undefined, metadata);
          }),
        ),
      ),
    );
  }

  private resolveExistingProject(project: ScopedProject) {
    return this.projectService.findLocalProjectByCloudId(project.cloudId!);
  }

  private importProject(
    project: ScopedProject,
    newProjectCreationEnabled: boolean,
    existingProject?: ScopedProject,
    metadata?: CloudProjectMetadata,
  ) {
    if (!existingProject) {
      this.createNewProject(project, metadata);
      this.projectService.updateUserWritePermission(project);
      return;
    }
    this.projectService.updateUserWritePermission(project);
    this.modalService
      .open(ProjectImportConfirmModalComponent, {
        data: { newProjectCreationEnabled },
      })
      .onClose$.subscribe((result: ProjectImportConfirmResult) => {
        if (result == ProjectImportConfirmResult.OVERRIDE_PROJECT) {
          this.overrideProject(project, metadata);
        } else if (result == ProjectImportConfirmResult.NEW_PROJECT) {
          // copy project in order to get a new id and name
          this.createNewProject(requireScopedProject(project.copy()));
        } else {
          console.error(`Unexpected project import confirm result: ${result}`);
        }
      });
  }

  private overrideProject(importProject: ScopedProject, metadata?: CloudProjectMetadata) {
    this.projectService.update(importProject, metadata).subscribe({
      next: () => {
        this.goToProjectDetails(importProject);
        this.toastHelper.success(
          $localize`:@@toast.projectImport.overwriteSuccessful:Projekt '${importProject.name}:projectName:' wurde erfolgreich überschrieben`,
        );
      },
      error: (err) => {
        console.error("Failed to overwrite imported project:", err);
        this.toastHelper.error(
          $localize`:@@toast.projectImport.overwriteFailed.header:Projektüberschreibung fehlgeschlagen`,
          $localize`:@@toast.projectImport.overwriteFailed.message:Projekt '${importProject.name}:projectName:' konnte nicht überschrieben werden. Bitte prüfe die Projektdatei.`,
        );
      },
    });
  }

  private createNewProject(project: ScopedProject, metadata?: CloudProjectMetadata) {
    this.projectService
      .createProject(project, metadata)
      .pipe(
        tap((createdProject) => this.productCountryService.selectCountry(createdProject.country)),
        switchMap((createdProject) =>
          this.projectService.updateUserWritePermission(createdProject).pipe(map(() => createdProject)),
        ),
      )
      .subscribe({
        next: (createdProject) => {
          this.goToProjectDetails(createdProject);
          this.toastHelper.success(
            $localize`:@@toast.projectImport.importSuccessful:Projekt '${createdProject.name}:projectName:' wurde erfolgreich importiert`,
          );
        },
        error: (err) => {
          console.error("Project import failed:", err);
          this.toastHelper.error(
            $localize`:@@toast.projectImport.importFailed.header:Projektimport fehlgeschlagen`,
            $localize`:@@toast.projectImport.importFailed.message:Projekt '${project.name}:projectName:' konnte nicht importiert werden.`,
          );
        },
      });
  }

  private goToProjectDetails(project: ScopedProject) {
    this.router.navigate(["projects", project.id]);
  }
}
